<template>
  <div class="notice-preview">
    <div class="preview-header">
      <span class="preview-title">
        <i class="el-icon-mobile-phone"></i>
        <span>小程序效果预览</span>
      </span>
      <span class="preview-tip">实时跟随左侧配置</span>
    </div>

    <!-- 场景切换 -->
    <div class="scene-tabs">
      <span
        v-for="item in sceneList"
        :key="item.value"
        class="scene-tab"
        :class="{ active: scene === item.value }"
        @click="goScene(item.value)"
      >{{ item.label }}</span>
    </div>

    <!-- 手机外壳 -->
    <div class="phone-shell">
      <div class="phone-screen">
        <div class="phone-notch"></div>
        <!-- 状态栏 -->
        <div class="phone-status">
          <span class="status-time">9:41</span>
          <span class="status-right">
            <span class="signal">
              <i class="bar b1"></i><i class="bar b2"></i><i class="bar b3"></i>
            </span>
            <span class="battery"></span>
          </span>
        </div>
        <!-- 导航栏 -->
        <div class="phone-nav">
          <span class="nav-back">‹</span>
          <span class="nav-title">通知详情</span>
          <span class="nav-more">•••</span>
        </div>

        <!-- 页面主体 -->
        <div class="phone-body">
          <div class="notice-card" :class="cardClass">
            <div class="card-head">
              <span class="type-tag" :class="cardClass">{{ typeTagText }}</span>
              <span class="read-state" :class="{ unread: !checkedRead }" @click="checkedRead = !checkedRead">
                {{ checkedRead ? '已读' : '未读' }}
              </span>
            </div>
            <div class="notice-title">{{ form.noticeName || '（未填写通知名称）' }}</div>
            <div class="notice-meta">
              <div class="meta-row">
                <span class="meta-icon">📢</span>
                <span class="meta-label">发布时间</span>
                <span class="meta-value">{{ publishTimeText }}</span>
              </div>
              <div class="meta-row">
                <span class="meta-icon">⏰</span>
                <span class="meta-label">截止时间</span>
                <span class="meta-value">{{ deadlineTimeText }}</span>
              </div>
            </div>
            <div class="notice-content" :class="{ 'is-empty': !contentHtml }">
              <div v-if="contentHtml" v-html="contentHtml"></div>
              <div v-else class="empty-tip">（通知正文为空，在左侧输入后这里实时预览）</div>
            </div>
          </div>

          <!-- 建议入口 -->
          <div class="suggest-entry" v-if="isActivity && form.suggestOpen === 1">
            <span class="suggest-icon">📝</span>
            <div class="suggest-main">
              <span class="suggest-text">{{ form.suggestText || '您有什么建议？' }}</span>
              <span class="suggest-deadline" v-if="form.deadlineTime">截止 {{ deadlineTimeText }}</span>
            </div>
            <span class="suggest-arrow">›</span>
          </div>
        </div>

        <!-- 底部操作条：活动通知 -->
        <div class="phone-footer" v-if="isActivity">
          <div class="confirm-check" v-if="form.confirmTextOpen === 1 && form.confirmText">
            <span class="checkbox" :class="{ checked: confirmChecked }" @click="confirmChecked = !confirmChecked"></span>
            <span class="check-text">{{ form.confirmText }}</span>
          </div>
          <div class="footer-btns">
            <span class="btn sub" @click="goScene('reason')">{{ form.subButtonName || '不参加' }}</span>
            <span class="btn main" :class="{ 'is-disabled': isCounting }" @click="onMainButtonClick('result')">{{ form.mainButtonName || '参加' }}{{ countdownText }}</span>
          </div>
        </div>
        <!-- 底部操作条：系统通知 -->
        <div class="phone-footer" v-else>
          <div class="footer-btns">
            <span class="btn main" :class="{ 'is-disabled': isCounting }" @click="onMainButtonClick('result')">我知道了{{ countdownText }}</span>
          </div>
        </div>

        <!-- ===== 首页弹窗 ===== -->
        <div class="phone-overlay" v-if="scene === 'homePop'">
          <div class="pop-card" v-if="form.isOpenHomePop === 1">
            <div class="pop-title">{{ form.homePopTitle || form.noticeName || '通知标题' }}</div>
            <div class="pop-body" :class="{ 'is-empty': !popContentHtml }">
              <div v-if="popContentHtml" v-html="popContentHtml"></div>
              <div v-else class="empty-tip">（弹窗内容为空）</div>
            </div>
            <div class="pop-btns">
              <span
                class="btn sub"
                v-if="form.homePopSubOpen === 1 && form.homePopSubButtonName"
                @click="goScene('detail')"
              >{{ form.homePopSubButtonName }}</span>
              <span class="btn main" @click="goScene('detail')">{{ form.homePopMainButtonName || '查看详情' }}</span>
            </div>
          </div>
          <div class="pop-card" v-else>
            <div class="pop-title">未开启首页弹窗</div>
            <div class="pop-desc">在左侧「首页弹窗」勾选“开启首页弹窗”后，即可预览进店弹窗效果</div>
          </div>
        </div>

        <!-- ===== 不参加原因弹窗 ===== -->
        <div class="phone-overlay" v-if="scene === 'reason'">
          <div class="pop-card">
            <div class="pop-title">{{ form.negTitle || '不参加原因反馈' }}</div>
            <div class="pop-desc" v-if="form.negDesc">{{ form.negDesc }}</div>
            <div class="pop-reasons">
              <div
                class="reason-item"
                v-for="(reason, index) in reasonList"
                :key="index"
                @click="toggleReason(reason)"
              >
                <span
                  class="radio"
                  :class="{ checked: selectedReasons.indexOf(reason) > -1, 'is-checkbox': !reasonSingle }"
                ></span>
                <span class="reason-text">{{ reason }}</span>
              </div>
              <div class="empty-tip" v-if="!reasonList.length">（左侧「理由选项」为空）</div>
            </div>
            <div class="reason-extra-title">补充说明（选填）</div>
            <div class="reason-textarea" contenteditable="true" data-placeholder="可补充说明不参加的原因"></div>
            <div class="pop-btns">
              <span class="btn sub" @click="goScene('detail')">{{ form.negCancelButtonName || '再想想' }}</span>
              <span class="btn main" @click="goScene('result')">{{ form.negConfirmButtonName || '确认不参加' }}</span>
            </div>
          </div>
        </div>

        <!-- ===== 结果 / 回执弹窗 ===== -->
        <div class="phone-overlay" v-if="scene === 'result'">
          <div class="pop-card small">
            <template v-if="isActivity">
              <div class="result-icon">✓</div>
              <div class="pop-title">已上报总部运营中心</div>
              <div class="pop-desc">已上报总部运营中心，感谢您的参加！</div>
            </template>
            <template v-else>
              <div class="result-icon plain">📖</div>
              <div class="pop-title">已读回执</div>
              <div class="pop-desc">您已确认阅读本公告，结果已同步至总部。</div>
            </template>
            <div class="pop-btns">
              <span class="btn main" @click="goScene('detail')">{{ isActivity ? '知道了' : '我知道了' }}</span>
            </div>
          </div>
        </div>

        <!-- ===== 建议弹窗 ===== -->
        <div class="phone-overlay" v-if="scene === 'suggest'">
          <div class="pop-card">
            <div class="pop-title">{{ form.suggestText || '向总部提建议' }}</div>
            <div class="pop-desc" v-if="form.deadlineTime">截止 {{ deadlineTimeText }}</div>
            <div
              class="reason-textarea tall"
              contenteditable="true"
              data-placeholder="请输入您的建议（500字以内）"
              @input="onEditableInput('suggestText', $event)"
            ></div>
            <div class="word-count">{{ suggestText.length }}/500</div>
            <div class="pop-btns">
              <span class="btn sub" @click="goScene('detail')">取消</span>
              <span class="btn main" @click="goScene('detail')">提交</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="preview-foot-tip">预览为静态示意，交互细节以真机为准</div>
  </div>
</template>

<script>
/** 带 HTML 标签的内容（后台富文本编辑器产出） */
const HTML_TAG_RE = /<\s*\/?\s*(a|b|blockquote|br|code|div|em|h[1-6]|hr|i|img|li|ol|p|pre|s|span|strike|strong|u|ul)\b[^>]*>/i

export default {
  name: 'NoticePhonePreview',
  props: {
    form: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      scene: 'detail',
      checkedRead: false,
      confirmChecked: false,
      selectedReasons: [],
      suggestText: '',
      // 主按钮倒计时剩余秒数（0 表示已走完、可点击）
      countdownLeft: 0,
      countdownTimer: null
    }
  },
  computed: {
    isActivity() {
      return Number(this.form.noticeType) === 1
    },
    cardClass() {
      return this.isActivity ? 'tag-activity' : 'tag-system'
    },
    typeTagText() {
      return this.isActivity ? '活动通知' : '系统通知'
    },
    sceneList() {
      const list = [
        { value: 'detail', label: '通知详情' },
        { value: 'homePop', label: '首页弹窗' }
      ]
      if (this.isActivity && this.form.isOpenNegReason === 1) {
        list.push({ value: 'reason', label: '原因弹窗' })
      }
      list.push({ value: 'result', label: this.isActivity ? '提交结果' : '已读回执' })
      if (this.isActivity && this.form.suggestOpen === 1) {
        list.push({ value: 'suggest', label: '建议弹窗' })
      }
      return list
    },
    publishTimeText() {
      if (this.form.publishType === 2) {
        return this.formatTime(this.form.publishTime) || '待发布'
      }
      return '发布后展示'
    },
    deadlineTimeText() {
      // 报名截止时间已移到列表“发布”弹窗，编辑阶段无值，这里提示发布时设置
      return this.formatTime(this.form.deadlineTime) || '发布时设置'
    },
    countdownEnabled() {
      return Number(this.form.countdownConfirmOpen) === 1 && Number(this.form.countdownConfirmSeconds) > 0
    },
    isCounting() {
      return this.countdownEnabled && this.countdownLeft > 0
    },
    countdownText() {
      return this.isCounting ? `（${this.countdownLeft}s）` : ''
    },
    contentHtml() {
      return this.toPreviewHtml(this.form.content)
    },
    popContentHtml() {
      return this.toPreviewHtml(this.form.homePopContent)
    },
    reasonList() {
      return (this.form.negReasons || []).filter(item => item && String(item).trim())
    },
    reasonSingle() {
      return Number(this.form.reasonSelectType) === 1
    }
  },
  watch: {
    // 倒计时开关/秒数变化后按新配置重新计时
    'form.countdownConfirmOpen'() {
      this.startCountdown()
    },
    'form.countdownConfirmSeconds'() {
      this.startCountdown()
    },
    sceneList(list) {
      // 配置变化导致当前场景被移除时（如关闭理由选项），自动回到详情
      if (!list.some(item => item.value === this.scene)) {
        this.goScene('detail')
      }
    },
    reasonList() {
      // 选项被删掉后同步清理已选中的理由
      this.selectedReasons = this.selectedReasons.filter(item => this.reasonList.indexOf(item) > -1)
    }
  },
  mounted() {
    this.startCountdown()
  },
  beforeDestroy() {
    this.stopCountdown()
  },
  methods: {
    // 切换场景；回到通知详情时重新开始主按钮倒计时，方便对照效果
    goScene(scene) {
      this.scene = scene
      if (scene === 'detail') {
        this.startCountdown()
      }
    },
    // 倒计时未走完时主按钮不响应点击
    onMainButtonClick(scene) {
      if (this.isCounting) return
      this.goScene(scene)
    },
    // 主按钮倒计时：每秒递减，归零后按钮恢复可点
    startCountdown() {
      this.stopCountdown()
      const seconds = Number(this.form.countdownConfirmSeconds) || 0
      if (Number(this.form.countdownConfirmOpen) !== 1 || seconds <= 0) {
        this.countdownLeft = 0
        return
      }
      this.countdownLeft = seconds
      this.countdownTimer = setInterval(() => {
        if (this.countdownLeft <= 1) {
          this.countdownLeft = 0
          this.stopCountdown()
        } else {
          this.countdownLeft -= 1
        }
      }, 1000)
    },
    stopCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },
    // 预览里的可输入区（建议正文等）：只做字数统计，不做真实提交
    onEditableInput(key, event) {
      const text = (event.target.innerText || '').replace(/\n+$/, '')
      this[key] = text.slice(0, 500)
    },
    toggleReason(reason) {
      if (this.reasonSingle) {
        this.selectedReasons = this.selectedReasons[0] === reason ? [] : [reason]
        return
      }
      const index = this.selectedReasons.indexOf(reason)
      if (index > -1) {
        this.selectedReasons.splice(index, 1)
      } else {
        this.selectedReasons.push(reason)
      }
    },
    formatTime(val) {
      if (!val) return ''
      const str = String(val).replace(/-/g, '/')
      const date = new Date(str)
      if (isNaN(date.getTime())) return String(val)
      const pad = num => String(num).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    },
    escapeHtml(text) {
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    // 正文：后台富文本产出直接渲染；纯文本兜底转义 + 换行，避免换行被折叠
    toPreviewHtml(raw) {
      if (raw === null || raw === undefined || raw === '') return ''
      const content = String(raw).replace(/<script[\s\S]*?<\/script>/gi, '').replace(/<style[\s\S]*?<\/style>/gi, '')
      if (!HTML_TAG_RE.test(content)) {
        return content
          .replace(/\r\n/g, '\n')
          .replace(/\r/g, '\n')
          .trim()
          .split(/\n{2,}/)
          .filter(block => block.trim() !== '')
          .map(block => `<p>${this.escapeHtml(block.trim()).replace(/\n/g, '<br/>')}</p>`)
          .join('')
      }
      return content
    }
  }
}
</script>

<style scoped>
.notice-preview {
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  padding: 14px;
}
.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f1f4;
}
.preview-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}
.preview-title i {
  margin-right: 4px;
  color: #ff7a45;
}
.preview-tip {
  font-size: 12px;
  color: #a6adb8;
}
/* 场景切换 */
.scene-tabs {
  display: flex;
  flex-wrap: wrap;
  margin: 10px 0;
}
.scene-tab {
  padding: 3px 9px;
  margin: 0 6px 6px 0;
  font-size: 12px;
  color: #606266;
  background: #f4f5f7;
  border-radius: 999px;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s;
}
.scene-tab:hover {
  color: #ff7a45;
}
.scene-tab.active {
  color: #fff;
  background: linear-gradient(135deg, #ff7a45, #ff5722);
  box-shadow: 0 2px 8px rgba(255, 122, 69, 0.3);
}

/* ===== 手机外壳 ===== */
.phone-shell {
  width: 100%;
  max-width: 320px;
  height: min(660px, calc(100vh - 300px));
  min-height: 480px;
  margin: 0 auto;
  padding: 8px;
  background: #1f2329;
  border-radius: 34px;
  box-shadow: 0 10px 30px rgba(17, 24, 39, 0.25);
  box-sizing: border-box;
}
.phone-screen {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f6f8;
  border-radius: 27px;
  overflow: hidden;
}
.phone-notch {
  position: absolute;
  top: 5px;
  left: 50%;
  width: 44px;
  height: 5px;
  margin-left: -22px;
  border-radius: 3px;
  background: #1f2329;
  z-index: 5;
}
.phone-status {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 24px;
  padding: 0 12px;
  font-size: 10px;
  color: #1f2329;
}
.status-right {
  display: flex;
  align-items: center;
}
.signal {
  display: flex;
  align-items: flex-end;
  margin-right: 4px;
}
.signal .bar {
  width: 2px;
  margin-left: 1px;
  background: #1f2329;
  border-radius: 1px;
}
.signal .b1 { height: 4px; }
.signal .b2 { height: 6px; }
.signal .b3 { height: 8px; }
.battery {
  width: 15px;
  height: 8px;
  border: 1px solid #1f2329;
  border-radius: 2px;
  position: relative;
}
.battery::after {
  content: '';
  position: absolute;
  left: 1px;
  top: 1px;
  bottom: 1px;
  width: 8px;
  background: #1f2329;
  border-radius: 1px;
}
.phone-nav {
  flex: none;
  position: relative;
  display: flex;
  align-items: center;
  height: 34px;
  padding: 0 10px;
  background: #fff;
  border-bottom: 1px solid #f0f1f4;
}
.nav-back {
  font-size: 20px;
  line-height: 1;
  color: #1f2329;
}
.nav-title {
  position: absolute;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: #1f2329;
}
.nav-more {
  margin-left: auto;
  font-size: 10px;
  letter-spacing: 1px;
  color: #1f2329;
}

/* ===== 页面主体 ===== */
.phone-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px 12px 132px;
  /* 隐藏滚动条，保留下滑查看能力 */
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.phone-body::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}
.notice-card {
  position: relative;
  overflow: hidden;
  background: #fff;
  border-radius: 12px;
  padding: 16px 14px 14px;
  box-shadow: 0 2px 10px rgba(17, 24, 39, 0.05);
}
.notice-card::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 4px;
  background: linear-gradient(90deg, #ff7a45, #ffb27a);
}
.notice-card.tag-system::before {
  background: linear-gradient(90deg, #4a7cf6, #8ab0ff);
}
.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.type-tag {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
}
.type-tag.tag-activity {
  color: #ff7a45;
  background: rgba(255, 122, 69, 0.12);
}
.type-tag.tag-system {
  color: #4a7cf6;
  background: rgba(74, 124, 246, 0.12);
}
.read-state {
  font-size: 11px;
  color: #a6adb8;
  cursor: pointer;
}
.read-state.unread {
  color: #ff7a45;
  font-weight: 600;
}
.notice-title {
  margin-top: 10px;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: #1f2329;
  word-break: break-all;
}
.notice-meta {
  margin-top: 10px;
  padding: 8px 10px;
  background: #f8f9fb;
  border-radius: 8px;
}
.meta-row {
  display: flex;
  align-items: center;
  font-size: 10.5px;
  line-height: 18px;
  color: #6b7280;
}
.meta-icon {
  margin-right: 4px;
}
.meta-label {
  color: #a6adb8;
}
.meta-value {
  margin-left: 6px;
  color: #6b7280;
}
.notice-content {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f1f4;
  font-size: 12.5px;
  line-height: 21px;
  color: #333;
  word-break: break-all;
}
.notice-content ::v-deep p {
  margin: 0 0 8px;
}
.notice-content ::v-deep p:last-child {
  margin-bottom: 0;
}
.notice-content ::v-deep h1,
.notice-content ::v-deep h2,
.notice-content ::v-deep h3 {
  margin: 6px 0;
  font-size: 14px;
  line-height: 22px;
}
.notice-content ::v-deep ul,
.notice-content ::v-deep ol {
  margin: 6px 0;
  padding-left: 18px;
  list-style: disc;
}
.notice-content ::v-deep ol {
  list-style: decimal;
}
.notice-content ::v-deep li {
  margin-bottom: 2px;
}
.notice-content ::v-deep blockquote {
  margin: 6px 0;
  padding: 2px 0 2px 8px;
  border-left: 3px solid #ffd0b8;
  color: #6b7280;
}
.notice-content ::v-deep img {
  max-width: 100%;
}
.notice-content ::v-deep strong {
  font-weight: 700;
}
.empty-tip {
  color: #b8bec6;
  font-size: 11px;
  line-height: 20px;
}

/* 建议入口 */
.suggest-entry {
  display: flex;
  align-items: center;
  margin-top: 12px;
  padding: 13px 14px;
  background: linear-gradient(135deg, #fff6f0 0%, #ffffff 72%);
  border: 1px solid rgba(255, 122, 69, 0.22);
  border-radius: 12px;
}
.suggest-icon {
  width: 32px;
  height: 32px;
  margin-right: 10px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  background: rgba(255, 122, 69, 0.14);
}
.suggest-main {
  display: flex;
  flex-direction: column;
}
.suggest-text {
  font-size: 13px;
  font-weight: 600;
  color: #ff7a45;
}
.suggest-deadline {
  margin-top: 2px;
  font-size: 10px;
  color: #a6adb8;
}
.suggest-arrow {
  margin-left: auto;
  font-size: 16px;
  color: #ffb491;
}

/* ===== 底部操作条 ===== */
.phone-footer {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 12px 14px;
  background: #fff;
  box-shadow: 0 -3px 12px rgba(17, 24, 39, 0.06);
}
.confirm-check {
  display: flex;
  align-items: flex-start;
  margin-bottom: 10px;
}
.checkbox {
  flex: none;
  width: 15px;
  height: 15px;
  margin: 1px 8px 0 0;
  border: 1px solid #cfd4db;
  border-radius: 50%;
  position: relative;
  cursor: pointer;
}
.checkbox.checked {
  border-color: #ff7a45;
  background: #ff7a45;
}
.checkbox.checked::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 1px;
  width: 4px;
  height: 8px;
  border: 1px solid #fff;
  border-left: 0;
  border-top: 0;
  transform: rotate(45deg);
}
.check-text {
  font-size: 11px;
  line-height: 18px;
  color: #6b7280;
  word-break: break-all;
}
.footer-btns {
  display: flex;
  align-items: center;
}
.btn {
  flex: 1;
  height: 38px;
  border-radius: 19px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
}
.btn:active {
  opacity: 0.85;
}
.btn.sub {
  margin-right: 10px;
  background: #f4f5f7;
  color: #6b7280;
}
.btn.main {
  background: linear-gradient(135deg, #ff7a45, #ff5722);
  color: #fff;
  box-shadow: 0 4px 12px rgba(255, 122, 69, 0.28);
}
/* 倒计时未走完：主按钮置灰且不可点击 */
.btn.main.is-disabled {
  background: #dcdfe6;
  color: #fff;
  box-shadow: none;
  cursor: not-allowed;
}
.btn.main.is-disabled:active {
  opacity: 1;
}

/* ===== 浮层弹窗 ===== */
.phone-overlay {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  background: rgba(17, 24, 39, 0.55);
  box-sizing: border-box;
}
.pop-card {
  width: 100%;
  max-width: 258px;
  max-height: 88%;
  overflow-y: auto;
  padding: 20px 16px 16px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(17, 24, 39, 0.18);
  box-sizing: border-box;
  /* 弹窗内容超高时可滑动，但不显示滚动条 */
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.pop-card::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}
.pop-card.small {
  max-width: 218px;
  text-align: center;
}
.pop-title {
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: #1f2329;
  text-align: center;
  word-break: break-all;
}
.pop-desc {
  margin-top: 8px;
  font-size: 11px;
  line-height: 18px;
  color: #8a9099;
  text-align: center;
  word-break: break-all;
}
.pop-body {
  margin-top: 12px;
  padding: 12px 12px;
  background: #f8f9fb;
  border-radius: 9px;
  font-size: 12px;
  line-height: 20px;
  color: #4b5563;
  word-break: break-all;
}
.pop-body ::v-deep p {
  margin: 0 0 6px;
}
.pop-body ::v-deep p:last-child {
  margin-bottom: 0;
}
.pop-body ::v-deep img {
  max-width: 100%;
}
.pop-btns {
  display: flex;
  align-items: center;
  margin-top: 16px;
}
.pop-btns .btn {
  height: 36px;
  border-radius: 18px;
  font-size: 13px;
}
.pop-reasons {
  margin-top: 12px;
}
.reason-item {
  display: flex;
  align-items: center;
  padding: 9px 10px;
  margin-top: 6px;
  background: #f8f9fb;
  border-radius: 8px;
  cursor: pointer;
}
.reason-item:hover {
  background: #f2f4f7;
}
.radio {
  flex: none;
  width: 15px;
  height: 15px;
  margin-right: 8px;
  border: 1px solid #cfd4db;
  border-radius: 50%;
  background: #fff;
  position: relative;
}
.radio.is-checkbox {
  border-radius: 3px;
}
.radio.checked {
  border-color: #ff7a45;
  background: #ff7a45;
}
.radio.checked::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 1px;
  width: 4px;
  height: 8px;
  border: 1px solid #fff;
  border-left: 0;
  border-top: 0;
  transform: rotate(45deg);
}
.reason-text {
  font-size: 12px;
  line-height: 18px;
  color: #333;
  word-break: break-all;
}
.reason-extra-title {
  margin-top: 12px;
  font-size: 11px;
  color: #6b7280;
}
.reason-textarea {
  margin-top: 6px;
  padding: 9px 10px;
  min-height: 52px;
  background: #f8f9fb;
  border-radius: 8px;
  font-size: 11.5px;
  line-height: 18px;
  color: #333;
  word-break: break-all;
  outline: none;
  cursor: text;
}
.reason-textarea:focus {
  box-shadow: 0 0 0 1px rgba(255, 122, 69, 0.35);
}
.reason-textarea.tall {
  min-height: 72px;
}
/* contenteditable 为空时展示占位文案 */
.reason-textarea:empty::before {
  content: attr(data-placeholder);
  color: #b8bec6;
}
.word-count {
  margin-top: 4px;
  font-size: 10px;
  color: #b8bec6;
  text-align: right;
}
.result-icon {
  width: 42px;
  height: 42px;
  margin: 0 auto 10px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: #fff;
  background: linear-gradient(135deg, #66d17a, #4caf50);
  box-shadow: 0 6px 14px rgba(76, 175, 80, 0.28);
}
.result-icon.plain {
  width: auto;
  height: auto;
  font-size: 34px;
  background: transparent;
  box-shadow: none;
}
.preview-foot-tip {
  margin-top: 8px;
  font-size: 11px;
  color: #b8bec6;
  text-align: center;
}
</style>
